import React, { useEffect, useRef, useState } from 'react'
import { Canvas, extend, useFrame } from '@react-three/fiber'
import { AdaptiveDpr, useGLTF, Environment, Gltf } from "@react-three/drei";
import Async from "react-async"
import * as THREE from "three"
import { getProject } from '@theatre/core'

import studio from '@theatre/studio'
import extension from '@theatre/r3f/dist/extension'
import { SheetProvider, editable as e, PerspectiveCamera } from '@theatre/r3f'
import { TextGeometry } from "three/examples/jsm/geometries/TextGeometry.js";
import { FontLoader } from 'three/examples/jsm/Addons.js';
import GoodGameFont from './Good-Game_Regular.json';
import AnimationData from './animationData.json'

extend({ TextGeometry });
declare const process: any;

// our Theatre.js project sheet, we'll use this later
const demoSheet = getProject('Demo Project', { state: AnimationData }).sheet('Demo Sheet')
// @ts-ignore
const font = new FontLoader().parse(GoodGameFont);

if (process.env.NODE_ENV === 'development') {
  studio.initialize()
  studio.extend(extension)
}

/**
 * async wait time
 * @param {number} time unit ms
 * @example await waitTime(1000)
 * //will wait 1 second and do after things
 * @returns {Promise<boolean>}
 */
export const waitTime = async () => {
  await new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, 100);
  });
  return "";
};

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}

export function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}

function Airplane() {
  const planeRef = useRef();
  let time = 0;

  useFrame(() => {
    if (planeRef.current) {
      time += 0.02;
      planeRef.current.position.y = 30 + Math.sin(time) * 0.5; // Hover up and down
    }
  });

  return (
    <Gltf src={'airplane.glb'} scale={new THREE.Vector3(2.5, 2.5, 2.5)} position={new THREE.Vector3(50, 0, 192)} rotation={new THREE.Euler(0, -1.4, 0)} ref={planeRef} />
  );
}

function Ocean() {
  const oceanRef = useRef();
  let time = 0;
  const gltf = useGLTF('water.glb');
  useFrame(() => {
    if (oceanRef.current) {
      time += 0.02;
      oceanRef.current.scale.y = Math.sin(time * 0.1) * 3;
      const material = oceanRef.current.children[0].material
      if (material) {
        // Access the texture and update its offset
        // material.map.offset.x += 0.005; // Adjust speed as needed
        material.map.offset.y += 0.00015; // Adjust speed as needed
      }
    }
  });

  return (
    <e.primitive ref={oceanRef} object={gltf.scene} theatreKey={'water'} editableType='mesh' />
  );
}

const OutlineText: React.FC<{ text: string, color: string, outlineColor: string, theatreKey?: string }> = ({ text, color = '#FFA500', outlineColor = '#fff', theatreKey }) => {
  return (
    <e.group theatreKey={theatreKey ?? text} scale={new THREE.Vector3(0.4, 0.4, 0.01)}>
      <mesh>
        {/* @ts-ignore */}
        <textGeometry args={[text, { font, color: '#fff' }]} />
        <meshBasicMaterial attach='material' color={color} />
      </mesh>
      <e.mesh theatreKey={`${text}outline`} position={new THREE.Vector3(0, -3, -55)}>
        {/* @ts-ignore */}
        <textGeometry args={[text, { font, color: '#fff' }]} />
        <meshBasicMaterial attach='material' color={outlineColor} />
      </e.mesh>
    </e.group>
  )
}

function Cloud() {
  const gltfRef = useRef();
  useEffect(() => {
    if (gltfRef.current) {
      (gltfRef.current.children[0].material as THREE.MeshStandardMaterial).emissive = new THREE.Color('#fff');
      (gltfRef.current.children[0].material as THREE.MeshStandardMaterial).emissiveIntensity = 0.8;
    }
  }, [gltfRef]);

  const rndScale = Math.random() * 3 + 0.2
  const [position, setPosition] = useState([
    25 * (rndScale ^ 4), // Random x position
    Math.random() * 140 + 2,  // Random y position
    Math.random() * -220 + 280,    // Random z position (far away)
  ]);
  const [randomNum, setRandomNum] = useState(Math.floor(Math.random() * 5) + 1)
  const [randomScale, setRandomScale] = useState(rndScale)
  const [randomRot, setRandomRot] = useState(Math.floor(Math.random() * 15) + 75)

  useFrame(() => {
    setPosition((prev) => {
      const newZ = prev[2] - 0.04 / randomScale; // Move cloud forward
      if (newZ < 0) {
        // Respawn cloud at a random position when it moves out of view
        const rndScale = Math.random() * 3 + 0.2
        setRandomScale(rndScale);
        setRandomNum(Math.floor(Math.random() * 5) + 1)
        setRandomRot(Math.floor(Math.random() * 15) + 75)

        return [
          25 * (rndScale ^ 4), // Random x position
          Math.random() * 140 + 2,  // Random y position
          Math.random() * -50 + 500,    // Reset z position far away
        ];
      }
      return [prev[0], prev[1], newZ];
    });
  });

  return (
    <group position={position}>

      <Gltf ref={gltfRef} scale={randomScale} rotation={new THREE.Euler(0, randomRot / 180 * Math.PI, 0)} src={`clouds${randomNum}.glb`} />
      <ambientLight intensity={0.4} color={new THREE.Color('#b1d5fa')} position={new THREE.Vector3(0, -2, -2)} />
    </group>
  );
}



const CanvasItem = () => {
  const ref = useRef<THREE.Color | any>()
  //@ts-ignore
  const font = new FontLoader().parse(GoodGameFont);
  const sunRef = useRef();
  const targetRef = useRef(); // Ref for the editable.group

  const cameraRef = useRef();
  const mouse = useRef({ x: 0, y: 0 });



  const handleMouseMove = (event) => {
    const { innerWidth, innerHeight } = window;
    const x = (event.clientX / innerWidth - 0.5) * 2;
    const y = -(event.clientY / innerHeight - 0.5) * 2;
    mouse.current = { x, y };
  };

  React.useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    demoSheet.project.ready.then(() => demoSheet.sequence.play({ iterationCount: 1, range: [0, 11.14] }))
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Variables to control the fade-out and quick-in effect
  const fadeOutFactor = 1.4; // Controls how fast it fades out (smaller = slower fade)
  const quickInFactor = 0.4; // Controls how fast it snaps back in (larger = quicker)

  // Store the current rotation values
  const currentRotation = useRef({ x: 0, y: 0 });

  useFrame((_, delta) => {
    const strength = -0.1;

    if (cameraRef.current) {
      // Target rotation based on mouse position
      const targetX = mouse.current.y * strength;
      const targetY = mouse.current.x * strength;

      // Fade-out effect: Gradually reduce the influence of the current rotation
      currentRotation.current.x += (targetX - currentRotation.current.x) * fadeOutFactor * delta;
      currentRotation.current.y += (targetY - currentRotation.current.y) * fadeOutFactor * delta;

      // Quick-in effect: Snap closer to the target when the difference is small
      if (Math.abs(targetX - currentRotation.current.x) < 0.1) {
        currentRotation.current.x += (targetX - currentRotation.current.x) * quickInFactor * delta;
      }
      if (Math.abs(targetY - currentRotation.current.y) < 0.1) {
        currentRotation.current.y += (targetY - currentRotation.current.y) * quickInFactor * delta;
      }

      // Apply the smoothed rotation to the camera
      cameraRef.current.rotation.x = currentRotation.current.x;
      cameraRef.current.rotation.y = currentRotation.current.y;
    }
  });



  useEffect(() => {
    if (sunRef.current && targetRef.current) {
      // Set the target of the directional light to the editable.group
      sunRef.current.target = targetRef.current;
    }
  }, []); // Run once after the initial render

  return (
    <>
      <AdaptiveDpr pixelated />
      <group ref={cameraRef} >
        <PerspectiveCamera theatreKey="Camera" makeDefault position={[5, 5, -5]} fov={75} />
      </group>
      <Environment
        background
        // environmentIntensity={0}
        files={['Sky_left.png', 'Sky_right.png', 'Sky_top.png', 'Sky_bottom.png', 'Sky_front.png', 'Sky_back.png',]}
      />

      <e.group theatreKey='target' ref={targetRef} />
      <e.directionalLight
        theatreKey='sun'
        color={'#fff'}
        intensity={0}
        ref={sunRef}
        castShadow={true}
      />
      <ambientLight intensity={0.5} />
      <e.pointLight intensity={0.5} theatreKey='pointLight'
        castShadow={true}
      />
      <color ref={ref} attach="background" args={['#04c3f4']} />
      <e.fog attach="fog" args={[0x04c3f4, -4, 10]} theatreKey={'fog'} />
      <Ocean />
      <OutlineText text={'HOTEL COIN'} color={'#ec5d3c'} />
      <OutlineText theatreKey={"Decentralized Reward Points"} color={'#ec5d3c'} text={'Loyalty Reward Points'} />
      <OutlineText text={'Coming Soon'} color={'#fff'} outlineColor={'gray'} />
      <e.group theatreKey='planeAndOcean'>
        {Array.from({ length: 20 }).map((_, i) => (
          <Cloud key={i} />
        ))}
        <Airplane />
      </e.group>
    </>
  )

}
const PlaneAndOcean = () => {
  return (
    <Async promiseFn={waitTime}>
      <Async.Pending>Loading...</Async.Pending>
      <Async.Fulfilled>
        <Canvas
          shadows={'soft'}
          style={{ width: '100vw', height: '100vh', zIndex: '10', position: 'fixed' }}
          id='indexModel'
          children={
            <SheetProvider sheet={demoSheet} children={undefined}>
              <CanvasItem />
            </SheetProvider>
          }
        />

      </Async.Fulfilled>
    </Async>
  )
}

export default PlaneAndOcean
